import { useCallback, useEffect, useState } from 'react';
import { Button, FullScreenCover, Modal } from './primitives';
import { DocumentEditorView } from '../pages/DocumentEditor';
import { WorkItemDetailView } from '../pages/WorkItemDetail';

export type EditorCoverTarget =
  | { kind: 'document'; id: string }
  | { kind: 'workItem'; id: string };

export interface EditorCoverProps {
  /**
   * The item to show, or null when closed. Render this component
   * UNCONDITIONALLY and toggle `target` so the slide-down animates on close.
   */
  target: EditorCoverTarget | null;
  /** Swap the cover to a related item opened from inside the editor. */
  onNavigate: (target: EditorCoverTarget) => void;
  /** Close the cover (caller clears `target`; lists reload in place). */
  onClose: () => void;
}

/**
 * Hosts the full Document / Work Item editor inside a `FullScreenCover` that
 * slides up over the still-mounted page. The header offers "Open in editor"
 * (the standalone route) and a close button guarded against unsaved changes.
 */
export function EditorCover({ target, onNavigate, onClose }: EditorCoverProps) {
  const [dirty, setDirty] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  // Keep the last opened target so the editor keeps rendering during the
  // slide-down (when `target` has already gone null).
  const [last, setLast] = useState<EditorCoverTarget | null>(target);
  useEffect(() => {
    if (target) setLast(target);
  }, [target]);

  const open = target != null;
  const shown = target ?? last;

  const requestClose = useCallback(() => {
    if (dirty) {
      setConfirmOpen(true);
      return;
    }
    onClose();
  }, [dirty, onClose]);

  if (!shown) return null; // nothing has been opened yet

  const title = shown.kind === 'document' ? 'Document' : 'Work item';

  return (
    <FullScreenCover
      open={open}
      onClose={requestClose}
      title={title}
      bare
      ariaLabel={title}
      data-testid="editor-cover"
      closeTestId="editor-cover-close"
    >
      {shown.kind === 'document' ? (
        <DocumentEditorView
          key={shown.id}
          documentId={shown.id}
          surface="cover"
          onDirtyChange={setDirty}
        />
      ) : (
        <WorkItemDetailView
          key={shown.id}
          workItemId={shown.id}
          surface="cover"
          onOpenItem={(id) => onNavigate({ kind: 'workItem', id })}
          onDirtyChange={setDirty}
        />
      )}

      {confirmOpen ? (
        <Modal
          open
          onClose={() => setConfirmOpen(false)}
          title="Discard unsaved changes?"
          dataTestId="editor-cover-unsaved-modal"
          footer={
            <div className="pn-cover-modal-actions">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setConfirmOpen(false)}
                data-testid="editor-cover-unsaved-keep"
              >
                Keep editing
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => {
                  setConfirmOpen(false);
                  onClose();
                }}
                data-testid="editor-cover-unsaved-discard"
              >
                Discard &amp; close
              </Button>
            </div>
          }
        >
          <p>
            This editor has unsaved changes. Save them with the Save button
            before closing, or discard them to leave.
          </p>
        </Modal>
      ) : null}
    </FullScreenCover>
  );
}
